import { useMemo } from 'react'
import useBedbankOfferSearchPricing from 'hooks/useBedbankOfferSearchPricing'
import { useAppSelector } from 'hooks/reduxHooks'
import { isEligibleMetasearchCugRateRule } from 'lib/metasearch/metasearchRateRules'

const RULE_KEYWORDS = {
  MEMBER: 'member',
  LUX: 'lux_',
} as const

interface PromotesCugRateParams {
  ruleId?: string
  b2CPrice: number
  cugPrice?: number
  priceTotal?: number
  currentRegionCode: string
  userCountry?: string
}

interface ShouldMakeCugRateProminentParams extends PromotesCugRateParams {
  isLoggedIn: boolean
  utmBp?: string
}

interface UseProminentMetaSearchRateParams {
  isLoggedIn: boolean
  offer: App.BedbankOffer | App.BedbankOfferSummary
  filters?: App.OfferListFilters
  utmBp?: string
  ruleId?: string
  priceTotal?: number
  userCountry?: string
}

interface PricingPositionParams {
  primaryFilters: App.OfferListFilters | undefined
  secondaryFilters: App.OfferListFilters | undefined
  primaryPrice: ReturnType<typeof useBedbankOfferSearchPricing>
  secondaryPrice: ReturnType<typeof useBedbankOfferSearchPricing>
  primaryActionUtmBp: string | undefined
  secondaryActionUtmBp: string | undefined
  primaryVariant: 'default' | 'meta'
  secondaryVariant: 'default' | 'meta'
  isProminent: boolean
  cugPrice: number | undefined
  b2cPrice: number | undefined
}

export const promotesCugRate = ({
  ruleId,
  b2CPrice,
  cugPrice,
  priceTotal,
  currentRegionCode,
  userCountry,
}: PromotesCugRateParams): boolean => {
  if (ruleId === null || !cugPrice || cugPrice <= 0) {
    return false
  }

  if (ruleId?.includes(RULE_KEYWORDS.MEMBER)) {
    return true // if member rate requested
  }

  if (!priceTotal) {
    return false
  }

  if (!userCountry || currentRegionCode.toUpperCase() !== userCountry.toUpperCase()) {
    return false
  }

  if (ruleId?.includes(RULE_KEYWORDS.LUX)) {
    const b2CDiff = Math.abs(priceTotal - b2CPrice)
    const cugDiff = Math.abs(priceTotal - cugPrice)
    return cugDiff < b2CDiff // make cug price prominent if cug price is closer to the deep-linked url price
  }
  return false
}

export const shouldMakeCugRateProminent = ({
  utmBp,
  isLoggedIn,
  ruleId,
  b2CPrice,
  cugPrice,
  priceTotal,
  currentRegionCode,
  userCountry,
}: ShouldMakeCugRateProminentParams): boolean => {
  if (utmBp === '3') {
    return isEligibleMetasearchCugRateRule(ruleId) && !!cugPrice && cugPrice > 0
  }

  if (isLoggedIn) {
    return false
  }

  return promotesCugRate({
    ruleId,
    b2CPrice,
    cugPrice,
    priceTotal,
    currentRegionCode,
    userCountry,
  })
}

/**
 * Custom hook that encapsulates the CGU and B2C pricing logic for metasearch tiles.
 * Returns all dependent values based on the calculated makeCugRateProminent parameter
 * if makeCugRateProminent is true, cug rate will display prominently
 */
function useProminentMetaSearchRate({
  isLoggedIn,
  ruleId,
  offer,
  filters,
  utmBp,
  priceTotal,
  userCountry,
}: UseProminentMetaSearchRateParams): PricingPositionParams {
  const currentRegionCode = useAppSelector((state) => state.geo.currentRegionCode)
  const storedRuleId = useAppSelector((state) => state.utm.ruleId)
  const effectiveRuleId = ruleId ?? storedRuleId
  const isGhaCugRule = utmBp === '3' && isEligibleMetasearchCugRateRule(effectiveRuleId)
  const metaFilters = useMemo(
    () => ({ ...filters, source: 'metasearch' }),
    [filters],
  )
  const cugFilters = useMemo(
    () => ({
      ...filters,
      source: utmBp === '2' ? 'loggedInMetasearch' : undefined,
    }),
    [filters, utmBp],
  )

  const b2cRate = useBedbankOfferSearchPricing(offer, metaFilters)
  const cugRate = useBedbankOfferSearchPricing(offer, cugFilters, undefined, isGhaCugRule)

  const makeCugRateProminent = useMemo(() => {
    return shouldMakeCugRateProminent({
      utmBp,
      isLoggedIn,
      ruleId: effectiveRuleId,
      b2CPrice: b2cRate.offerPrice,
      cugPrice: cugRate.offerPrice,
      priceTotal,
      currentRegionCode,
      userCountry,
    })
  }, [effectiveRuleId, b2cRate.offerPrice, cugRate.offerPrice, priceTotal, currentRegionCode, userCountry, isLoggedIn, utmBp])

  const primaryPrice = makeCugRateProminent ? cugRate : b2cRate
  const secondaryPrice = makeCugRateProminent ? b2cRate : cugRate

  const primaryActionUtmBp = makeCugRateProminent ? utmBp : undefined
  const secondaryActionUtmBp = makeCugRateProminent ? undefined : utmBp

  const primaryFilters = makeCugRateProminent ? cugFilters : metaFilters
  const secondaryFilters = makeCugRateProminent ? metaFilters : cugFilters

  const primaryVariant = makeCugRateProminent ? 'default' : 'meta'
  const secondaryVariant = makeCugRateProminent ? 'meta' : 'default'

  return {
    primaryFilters,
    secondaryFilters,
    primaryPrice,
    secondaryPrice,
    primaryActionUtmBp,
    secondaryActionUtmBp,
    primaryVariant,
    secondaryVariant,
    isProminent: makeCugRateProminent,
    b2cPrice: b2cRate?.offerPrice,
    cugPrice: cugRate?.offerPrice,
  }
}

export default useProminentMetaSearchRate
